import React, { memo, useState, useRef, useEffect } from 'react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';

import TouchpointCard from 'components/candidate/TouchpointCard';
import { Checkbox } from 'components/form';
import { ButtonV3 } from 'components/ComponentV2';
import Logo from 'components/Logo';
import useScrollOffset from 'hooks/useScrollOffset';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { MatchAttributes } from 'lib/models/matches';
import { PAGE_ROUTES } from 'lib/page-routes';
import { CompanyAttributes } from 'lib/models/company';
import { StudentSocietyAttributes } from 'lib/models/student-society';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { TouchpointType } from 'lib/models/touchpoint';
import { OpportunityAttributes } from 'lib/models/opportunity';
import { Footer } from '../Footer/Footer.component';

import styles from '../Onboarding.module.scss';

export interface MatchesProps {
  /** Custom class name */
  className?: string;
  matchList: Array<MatchAttributes>;
  candidateSelectedMatches: Array<string>;
  onNextClick: (
    selectedMatches: Array<string>,
    isBackClicked?: boolean
  ) => void;
  isLoading: boolean;
  isBackLoading: boolean;
  onChangePreferenceClick: () => void;
}

export const Matches: React.FC<MatchesProps> = memo(
  ({
    className = '', // custom class name
    matchList,
    candidateSelectedMatches,
    onNextClick,
    isLoading,
    isBackLoading,
  }: MatchesProps) => {
    const router = useRouter();
    const [tCommon] = useTranslation('common');
    const [t] = useTranslation('candidate-onboarding');
    const { isWindowScrolled } = useScrollOffset();
    const bottomRef = useRef<HTMLDivElement>(null);

    const contentClassNames = cn(styles.container, className);

    const [selectedMatches, setSelectedMatches] = useState<Array<string>>([]);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const [fullyScrolled, setFullyScrolled] = useState(false);

    useEffect(() => {
      if (candidateSelectedMatches) {
        setSelectedMatches(candidateSelectedMatches);
      }
    }, [candidateSelectedMatches]);

    const onSubmit = (isBackClicked?: boolean) => {
      onNextClick(selectedMatches, isBackClicked);
    };

    useVisibleOnIntersect({
      elRef: bottomRef,
      threshold: 0,
      onIntersect: () => setFullyScrolled(true),
      onHidden: () => setFullyScrolled(false),
      rootMargin: '0px',
    });

    const onMatchCheckboxClick = (id: string) => {
      const index = selectedMatches?.findIndex((match) => match === id);
      if (index === -1) {
        setSelectedMatches([...selectedMatches, id]);
      } else {
        setSelectedMatches((list) => list.filter((res) => res !== id));
      }
    };

    if (matchList.length === 0) {
      return (
        <div
          className={contentClassNames}
          data-testid="Matches"
          ref={containerRef}
        >
          <div className={styles.content}>
            <p className={styles.setup}>{t('no-matches.title')}</p>
            <p className={styles.searchDesc}>{t('no-matches.desc')}</p>

            <ButtonV3
              color="secondary"
              size="xlarge"
              onClick={() =>
                router.push(
                  `${PAGE_ROUTES.CANDIDATE_ONBOARDING}?matching_preference=true`
                )
              }
              isLoading={isLoading}
              isFullWidth={true}
              className={styles.secondary}
            >
              {t('no-matches.change-preferences')}
            </ButtonV3>
            <ButtonV3
              size="xlarge"
              onClick={() => router.push(PAGE_ROUTES.CANDIDATE_PROFILE)}
              isLoading={isLoading}
              isFullWidth={true}
            >
              {t('no-matches.continue-anyways')}
            </ButtonV3>
          </div>
        </div>
      );
    }

    return (
      <div
        className={contentClassNames}
        data-testid="Matches"
        ref={containerRef}
      >
        <Link href={PAGE_ROUTES.CANDIDATE_DASHBOARD}>
          <div className={styles.header}>
            <div className={styles.wrapperLogoMatches}>
              <Logo name="huzzle-new" size="small" color="primary" />
              <ButtonV3
                color="secondary"
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedMatches(matchList.map((list) => list.id));
                }}
              >
                {tCommon('select-all')}
              </ButtonV3>
            </div>
          </div>
        </Link>
        <div className={styles.content}>
          <p className={styles.setup}>
            {t('matches.title', { matchListLength: matchList.length })}
          </p>
          <p className={styles.searchDesc}>{t('matches.desc')}</p>
          <div className={styles.gap}></div>
          {matchList.map(
            ({
              id,
              title,
              start_date,
              creatable_for,
              posted_at,
              matchable_created_at,
              nested_type,
              minimum_salary,
              maximum_salary,
              pay_currency,
              application_deadline,
              kind,
              cities,
              cohost_applications: cohostApplicationsResponse,
              matchable_slug: slug,
              touchpoint,
            }) => {
              const createdBy = parseResponse<
                CompanyAttributes | StudentSocietyAttributes
              >(creatable_for);
              const { name, icon_url, is_verified, huzzle_verified } =
                createdBy;
              const opportunity =
                parseResponse<OpportunityAttributes>(touchpoint);

              const cohost_applications = parseArrayResponse(
                cohostApplicationsResponse
              );
              return (
                <div
                  key={id}
                  className={styles.matchCard}
                  onClick={() => onMatchCheckboxClick(id)}
                >
                  <Checkbox
                    checked={selectedMatches?.some((item) => item === id)}
                    readOnly
                    hideLabel
                    className={styles.checkbox}
                  />
                  <TouchpointCard
                    icon_url={icon_url}
                    id={id}
                    slug={slug}
                    title={title}
                    createdBy={name}
                    startDate={start_date}
                    is_eligible_for_auto_apply={
                      opportunity.is_eligible_for_auto_apply
                    }
                    created_at={posted_at ? posted_at : matchable_created_at}
                    application_deadline={application_deadline}
                    is_verified={is_verified}
                    touchpointable_type={nested_type as TouchpointType}
                    maximum_salary={maximum_salary}
                    minimum_salary={minimum_salary}
                    pay_currency={pay_currency || 'GBP'}
                    url=""
                    isExpired={false}
                    className={styles.match}
                    kind={kind}
                    isOnboarding={true}
                    showManagerProgress={false}
                    huzzle_verified={huzzle_verified}
                    isNewSet={false}
                    cities={cities}
                    cohost_applications={cohost_applications}
                    following={null}
                    apply_url={null}
                    extension_installed={true}
                  />
                </div>
              );
            }
          )}
        </div>
        <div className={styles['bottom-ref']} ref={bottomRef}></div>

        <Footer
          isBackLoading={isBackLoading}
          isLoading={isLoading}
          onSubmit={onSubmit}
          isReady={Object.keys(selectedMatches).length > 0}
          isFullyScrolled={fullyScrolled}
          isWindowScrolled={isWindowScrolled}
        />
      </div>
    );
  }
);

Matches.displayName = 'Matches';
